/* eslint react/no-unused-state: 0 */

import { autobind } from 'core-decorators';
import moment from 'moment';
import React from 'react';
import ConsortiaDatesEnum from 'const/ConsortiaDatesEnum';
import ConsortiaReportTypeEnum from 'const/ConsortiaReportTypeEnum';
import { processing } from 'decorators';
import {
  getAccounts,
  getAccountsV3,
  getConsortiumStats,
  getDealOOConfiguration,
  createReport,
} from 'services/api/consortia';
import FeatureFlagsContext, { CONSORTIA_AVAILABLE_BALANCE_FLAG } from 'storage/FeatureFlagsContext';
import { withErrorNotification } from 'storage/NotificationsContext';
import mainWithContext from 'with/withContext';

type ConsortiaContextInterface = {
  accountName?: string;
  accounts?: {
    accounts: Array<{ account: ConsortiaAccount; stats: ConsortiaStats }>;
    pagination: Pagination;
    stats: ConsortiaStats;
  };
  dates: {
    id: ConsortiaDatesEnum;
    fromDate: string;
    toDate: string;
  };
  dealOOConfiguration?: any;

  page: number;
  pageSize: number;
  sorted?: string;

  fetchingAccountsError?: Error;
  isFetchingDealOOConfiguration: boolean;
  isLoadingAccounts: boolean;
  isRequestingReport: boolean;

  setConsortiumId: (consortiumId: number) => void;
  setActiveState: (activeState: boolean) => void;
  setConsortiumIdAndActiveState: (
    consortiumId: number,
    activeState: boolean,
    isAvailableBalanceEnabled?: boolean,
  ) => void;
  setDates: (id: string, fromDate: string, toDate: string) => void;
  setDatesIdWithoutReloading: (id: string) => void;
  onAccountsListChange: (sorted: string, page: number, pageSize: number, accountName?: string) => void;
  requestReport: (reportName: ConsortiaReportTypeEnum) => void;
};

const defaultValue: ConsortiaContextInterface = {
  dates: {
    id: ConsortiaDatesEnum.YTD,
    fromDate: moment().startOf('year').format('YYYY-MM-DD'),
    toDate: moment().format('YYYY-MM-DD'),
  },

  page: 0,
  pageSize: 10,

  isFetchingDealOOConfiguration: false,
  isLoadingAccounts: true,
  isRequestingReport: false,

  setConsortiumId: () => {},
  setActiveState: () => {},
  setConsortiumIdAndActiveState: () => {},
  setDates: () => {},
  setDatesIdWithoutReloading: () => {},
  onAccountsListChange: () => {},
  requestReport: () => {},
};

const Context = React.createContext<ConsortiaContextInterface>(defaultValue);
Context.displayName = 'ConsortiaContext';

function withProvider(WrappedComponent) {
  @autobind
  class ContextWrapper extends React.PureComponent<
    { isFeatureFlagEnabled: (flag: string) => boolean } & any,
    ConsortiaContextInterface & { consortiumId?: number; activeState?: boolean; isAvailableBalanceEnabled?: boolean }
  > {
    state = {
      ...defaultValue,
      consortiumId: undefined,
      activeState: undefined,
      isAvailableBalanceEnabled: undefined,
      onAccountsListChange: this.onAccountsListChange,
      requestReport: this.requestReport,
      setConsortiumId: this.setConsortiumId,
      setActiveState: this.setActiveState,
      setConsortiumIdAndActiveState: this.setConsortiumIdAndActiveState,
      setDates: this.setDates,
      setDatesIdWithoutReloading: this.setDatesIdWithoutReloading,
    };

    @withErrorNotification
    @processing('isFetchingDealOOConfiguration')
    async loadDealOOConfiguration() {
      const { activeState, consortiumId } = this.state;

      this.setState({ dealOOConfiguration: await getDealOOConfiguration(consortiumId, activeState ?? true) });
    }

    @withErrorNotification
    @processing('isLoadingAccounts')
    async loadConsortiaAccounts() {
      const { accountName, activeState, consortiumId, dates, isAvailableBalanceEnabled, page, pageSize, sorted } =
        this.state;
      const isFeatureFlagEnabled =
        isAvailableBalanceEnabled ?? this.props.isFeatureFlagEnabled(CONSORTIA_AVAILABLE_BALANCE_FLAG);

      try {
        if (isFeatureFlagEnabled) {
          // V3 API: Fetch consortium stats and accounts stats separately in parallel
          const [consortiumStats, accountsData] = await Promise.all([
            getConsortiumStats(consortiumId, activeState ?? true, dates.fromDate, dates.toDate),
            getAccountsV3(
              consortiumId,
              activeState ?? true,
              dates.fromDate,
              dates.toDate,
              sorted,
              page,
              pageSize,
              accountName,
            ),
          ]);

          // Merge the results to maintain backward compatibility with UI components
          this.setState({
            accounts: {
              ...accountsData,
              stats: consortiumStats,
            },
            fetchingAccountsError: null,
          });
        } else {
          // V2 API: Single call returns both accounts and stats
          this.setState({
            accounts: await getAccounts(
              consortiumId,
              activeState ?? true,
              dates.fromDate,
              dates.toDate,
              sorted,
              page,
              pageSize,
              accountName,
            ),
            fetchingAccountsError: null,
          });
        }
      } catch (error) {
        this.setState({ accounts: null, fetchingAccountsError: error });

        throw error;
      }
    }

    @withErrorNotification
    @processing('isRequestingReport')
    async requestReport(reportName: ConsortiaReportTypeEnum) {
      const { activeState, consortiumId, dates } = this.state;
      await createReport(consortiumId, activeState ?? true, reportName, dates.fromDate, dates.toDate);
    }

    setConsortiumId(consortiumId) {
      this.setState({ consortiumId, page: 0 }, () => {
        this.loadDealOOConfiguration();
        this.loadConsortiaAccounts();
      });
    }

    setActiveState(activeState) {
      this.setState({ activeState, page: 0 }, () => {
        this.loadDealOOConfiguration();
        this.loadConsortiaAccounts();
      });
    }

    setConsortiumIdAndActiveState(consortiumId, activeState, isAvailableBalanceEnabled) {
      this.setState({ consortiumId, activeState, isAvailableBalanceEnabled, page: 0 }, () => {
        this.loadDealOOConfiguration();
        this.loadConsortiaAccounts();
      });
    }

    setDatesIdWithoutReloading(id) {
      this.setState(prevState => ({ dates: { ...prevState.dates, id } }));
    }

    setDates(id, fromDate, toDate) {
      this.setState(
        {
          dates: {
            id,
            fromDate: moment(fromDate).format('YYYY-MM-DD'),
            toDate: moment(toDate).format('YYYY-MM-DD'),
          },
          page: 0,
        },
        this.loadConsortiaAccounts,
      );
    }

    onAccountsListChange(sorted, page, pageSize, accountName) {
      this.setState({ sorted, page, pageSize, accountName }, this.loadConsortiaAccounts);
    }

    render() {
      const { isFeatureFlagEnabled, ...restProps } = this.props;

      return (
        <Context.Provider value={this.state}>
          <WrappedComponent {...restProps} />
        </Context.Provider>
      );
    }
  }

  // Wrapper component that injects feature flags into ContextWrapper
  const ContextWrapperWithFeatureFlags: React.FC = props => {
    const { isEnabled } = FeatureFlagsContext.useContext();
    return <ContextWrapper {...props} isFeatureFlagEnabled={isEnabled} />;
  };

  return ContextWrapperWithFeatureFlags;
}

export { ConsortiaContextInterface };
export default {
  useContext: () => React.useContext(Context),
  withProvider,
  withContext: mainWithContext(Context),
};
